import { useCallback, useEffect, useRef, useState } from "react";
import { api, asArray, formatApiError } from "@/lib/api";
import { fmtTime } from "@/lib/time";
import { useAuth } from "@/context/AuthContext";
import { Paperclip, Download, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Deleting is gated one rung higher than uploading -- this mirrors
// backend/attachments.py's DELETE_ROLES exactly. super_admin/msp_owner pass
// every require_roles() check on the server regardless of the list, so they
// are included here too: the control being ABSENT must agree with what the
// server would actually do, not just with the named list.
const DELETE_ROLES = [
  "l2_technician", "l3_technician", "service_desk_manager",
  "super_admin", "msp_owner",
];

function formatSize(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n)) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// A download error arrives as a Blob (responseType: "blob"), so
// formatApiError -- which expects e.response.data to already be parsed JSON
// -- cannot read it. Read the blob back as text and parse it ourselves; this
// is the one place in this component that duplicates formatApiError's job,
// and it exists only because of the blob responseType.
async function formatBlobError(e, fallback) {
  const data = e?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      const d = parsed?.detail;
      if (typeof d === "string") return d;
      if (Array.isArray(d)) return d.map((x) => (x?.msg ? x.msg : JSON.stringify(x))).join(" ");
      if (d) return String(d);
    } catch { /* not JSON -- fall through to the generic message */ }
  }
  return formatApiError(e) || fallback;
}

/** Attachments on a ticket: list, upload, download, delete.
 *
 * Never rendered inline and never used to build an <img src> -- an
 * attachment's content_type is whatever the sniff decided, not a promise the
 * bytes are safe to display, so every open is a download.
 */
export default function AttachmentList({ ticketId, canWrite }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const canDelete = !!user && DELETE_ROLES.includes(user.role);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/tickets/${ticketId}/attachments`)
      .then((r) => setItems(asArray(r.data?.items)))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [ticketId]);
  useEffect(() => { load(); }, [load]);

  // The server resolves uploaded_by -> uploaded_by_name (one query per
  // request, not one per row) -- this used to hit /desk/workload for a
  // client-side guess, but that endpoint excludes super_admin/msp_owner, so
  // an admin's own upload rendered as "Former user" to every OTHER viewer.
  // The server knows who uploaded the file; asking it once is both simpler
  // and the only version of this that is never confidently wrong.
  const nameFor = (a) => {
    if (a.uploaded_by_name) return a.uploaded_by_name;
    if (a.source === "email") return "Received by email";
    return a.uploaded_by ? "Unknown" : "System";
  };

  const handleFiles = async (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post(`/tickets/${ticketId}/attachments`, form, {
        // The instance default forces application/json; unsetting it here
        // lets the browser set multipart/form-data with its own boundary.
        headers: { "Content-Type": undefined },
      });
      setItems((prev) => [...prev, data]);
      toast.success(`${data.filename} uploaded`);
    } catch (e) {
      // 413/415 land here as ordinary JSON error bodies -- the server's own
      // sentence ("Refused: the file begins with the bytes of...") is the
      // entire value of the control and must reach the screen verbatim.
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDownload = async (a) => {
    try {
      const res = await api.get(`/attachments/${a.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = a.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Deferred rather than synchronous: current Chromium hands the blob to
      // its download machinery before this line runs, but that is not a
      // guarantee every engine makes, and deferring costs nothing observable.
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (e) {
      toast.error(await formatBlobError(e, "Could not download the file"));
    }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Delete "${a.filename}"? This removes it from the ticket.`)) return;
    try {
      await api.delete(`/attachments/${a.id}`);
      setItems((prev) => prev.filter((x) => x.id !== a.id));
      toast.success("Attachment deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5" data-testid="ticket-attachments">
      <div className="flex items-center gap-2 mb-3">
        <Paperclip size={14} className="text-zinc-400" />
        <div className="font-display font-medium">Attachments</div>
        <div className="ml-auto font-mono text-xs text-zinc-500">{items.length}</div>
      </div>

      <div className="space-y-1.5">
        {loading ? (
          <div className="text-zinc-500 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-zinc-600 text-sm">No attachments yet.</div>
        ) : (
          items.map((a) => (
            <div key={a.id} data-testid={`attachment-row-${a.id}`}
              className="flex items-center gap-3 text-sm border border-zinc-900 rounded px-3 py-2 bg-zinc-900/30 flex-wrap">
              <span className="text-zinc-200 truncate min-w-0 flex-1" title={a.filename}>{a.filename}</span>
              <span className="text-[11px] text-zinc-500 font-mono shrink-0">{formatSize(a.size_bytes)}</span>
              <span className="text-[11px] text-zinc-500 shrink-0">{nameFor(a)}</span>
              <span className="text-[11px] text-zinc-500 font-mono shrink-0">
                {a.created_at ? fmtTime(a.created_at) : "—"}
              </span>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => handleDownload(a)} data-testid={`attachment-download-${a.id}`}
                  title="Download" aria-label={`Download ${a.filename}`}
                  className="text-zinc-400 hover:text-blue-400">
                  <Download size={14} />
                </button>
                {canDelete && (
                  <button onClick={() => handleDelete(a)} data-testid={`attachment-delete-${a.id}`}
                    title="Delete" aria-label={`Delete ${a.filename}`}
                    className="text-zinc-400 hover:text-red-400">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {canWrite && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <input ref={inputRef} type="file" data-testid="attachment-file-input"
            disabled={uploading}
            onChange={(e) => handleFiles(e.target.files)}
            className="text-xs text-zinc-400 file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-zinc-900 file:text-zinc-200 file:text-xs file:cursor-pointer hover:file:bg-zinc-800 disabled:opacity-50" />
          {uploading && (
            <span className="text-xs text-zinc-500 flex items-center gap-1">
              <Loader2 size={12} className="animate-spin" /> Uploading…
            </span>
          )}
        </div>
      )}
    </div>
  );
}
